// ============================================================
// THE DATA PLATE (plan armoury, stage 1).
//
// A weapon set out as type, for ANY weapon a GM invents: no drawing of the
// weapon itself (drawn glyphs were tried on the rack and rejected in play -
// the barrel read as a megaphone; WeaponPlate.tsx). The numbers sit in the
// cells a rulebook stat line would give them; the area, when there is one,
// is drawn as geometry, its spawned secondary on the primary's victims.
//
// This module is the plate's view: what it says, and the paths of the area
// figure in a 150 x 78 box. The component only lays them out.
//
// Pure. Zero React dependency.
// ============================================================
import type { CharacterWeapon } from '../character/types';
import { areaOf, expectedArea, type AreaShape, type ResolvedArea, type SpawnedBy } from '../areaShapes';
import { DEFAULT_SIGNATURE_REACH, reachOf, signatureOf, type Signature, type SignatureReach } from '../stealth/noise';
import type { Slot } from '../../types';
import type { Recipe } from './recipe';
import type { DerivedFigures } from './draft';
import { ruleSentence } from '../weaponRules';

interface PlateCell {
  label: string;
  value: string;
  /** The damage cell leads the line, larger than the rest. */
  lead?: boolean;
}

export interface AreaFigure {
  /** The primary template, filled faintly. */
  main: string;
  /** The secondary each spawner throws, dashed; '' with none. */
  spawn: string;
  /** The primary's victims, as dots. */
  victims: string;
  /** "BLAST 3 m · EACH VICTIM: BLAST 1 m". */
  say: string;
}

export interface PlateView {
  name: string;
  kind: string;
  cells: PlateCell[];
  signature: { word: string; reach: string };
  rules: string[];
  /** Its conditional rules as sentences (plan armoury): "Xenos-bane: +2 damage - when the target is Xenos". */
  conditional: string[];
  /** Its modules in order (plan armoury, stage 5), "① Suppressor"; empty for a weapon not forged. */
  modules: string[];
  area: AreaFigure | null;
}

const circle = (x: number, y: number, r: number) =>
  `M${x - r},${y} a${r},${r} 0 1,0 ${2 * r},0 a${r},${r} 0 1,0 ${-2 * r},0`;

/** Each shape in the box, with three victims where they would stand. */
const SHAPES: Record<Exclude<AreaShape, 'none'>, { main: string; at: [number, number][] }> = {
  blast: {
    main: circle(80, 39, 30),
    at: [
      [70, 33],
      [92, 26],
      [94, 50],
    ],
  },
  cone: {
    main: 'M8,39 L142,8 L142,70 Z',
    at: [
      [90, 34],
      [118, 46],
      [126, 24],
    ],
  },
  beam: {
    main: 'M8,34 L142,34 L142,44 L8,44 Z',
    at: [
      [60, 39],
      [100, 39],
      [132, 39],
    ],
  },
  arc: {
    main: 'M12,52 L54,26 L92,50 L132,22',
    at: [
      [54, 26],
      [92, 50],
      [132, 22],
    ],
  },
};

/** Which of the primary's victims spawn the secondary: all of them, the aimed one, or an arc's last leap. */
function spawners(at: [number, number][], by: SpawnedBy): [number, number][] {
  if (by === 'aimed') return at.slice(0, 1);
  if (by === 'last') return at.slice(-1);
  return at;
}

function secondaryAt([x, y]: [number, number], shape: AreaShape): string {
  if (shape === 'blast') return circle(x, y, 9);
  return `M${x},${y} L${x + 16},${y - 9} L${x + 16},${y + 9} Z`;
}

/** The sizes a resolved block or secondary carries, all numbers. */
type Sizes = Pick<ResolvedArea, 'radius' | 'length' | 'angle' | 'leaps'>;

const SIZE_OF: Record<Exclude<AreaShape, 'none'>, (a: Sizes) => string> = {
  blast: (a) => `${a.radius} m`,
  cone: (a) => `${a.length || 20} m · ${a.angle}°`,
  beam: (a) => `${a.length || 'its range'}${a.length ? ' m' : ''}`,
  arc: (a) => `${a.leaps} leap${a.leaps === 1 ? '' : 's'}`,
};

/** "BLAST 3 m" for a shaped block or secondary. */
const sizeSay = (shape: Exclude<AreaShape, 'none'>, a: Sizes) => `${shape.toUpperCase()} ${SIZE_OF[shape](a)}`;

function areaFigure(w: CharacterWeapon): AreaFigure | null {
  const block = areaOf(w);
  if (block.shape === 'none') return null;
  const a = expectedArea(block, w.specialRules);
  const shape = SHAPES[block.shape];
  const s = a.spawn && a.spawn.shape !== 'none' ? { ...a.spawn, shape: a.spawn.shape } : null;
  const said = sizeSay(block.shape, a);
  return {
    main: shape.main,
    spawn: s
      ? spawners(shape.at, s.spawnedBy)
          .map((p) => secondaryAt(p, s.shape))
          .join(' ')
      : '',
    victims: shape.at.map(([x, y]) => circle(x, y, 2.2)).join(' '),
    say: s ? `${said} · THEN ${sizeSay(s.shape, s)}` : said,
  };
}

const rateOfFire = (r: Slot['rateOfFire']) => [r.single ? 'S' : '–', r.semi || '–', r.full || '–'].join('/');

/** ①, ②, … - a module's number, as the plate and the rack print it (⑳ past twenty). */
const numbered = (i: number) => String.fromCodePoint(0x2460 + Math.min(i, 19));

/** A forged weapon's modules in order, "① Suppressor"; empty for a weapon not forged. */
const moduleLines = (recipe?: Recipe) => (recipe?.modules ?? []).map((m, i) => `${numbered(i)} ${m.module.name}`);

function cellsOf(w: CharacterWeapon, d: DerivedFigures | null): PlateCell[] {
  const lead = { label: 'DAMAGE', value: `${w.damageExpression} ${w.damageType}`, lead: true };
  const pen = { label: 'PEN', value: String(w.penetration) };
  if (w.type === 'melee') {
    const sb = w.strengthBonus ?? d?.strengthBonus;
    return [
      lead,
      pen,
      { label: 'REACH', value: `${w.reach || 1} m` },
      { label: 'SB', value: sb === undefined ? '–' : `+${sb}` },
    ];
  }
  return [
    lead,
    pen,
    { label: 'RANGE', value: `${w.range} m` },
    { label: 'ROF', value: rateOfFire(w.rateOfFire) },
    { label: 'CLIP', value: String(w.ammo.max) },
    { label: 'RELOAD', value: w.ammo.reloadAction || 'Full' },
  ];
}

/**
 * The plate for a weapon; `d` is what its character gives it (null for a statblock), `reach` how far each
 * Signature step carries at this table (the standard reach where no table says).
 */
export function plateView(
  w: CharacterWeapon,
  d: DerivedFigures | null,
  reach: SignatureReach = DEFAULT_SIGNATURE_REACH,
): PlateView {
  const step = signatureOf({ weaponType: w.type, noise: w.noise });
  return {
    name: w.name || 'Unnamed weapon',
    kind: w.type === 'melee' ? 'MELEE' : 'RANGED',
    cells: cellsOf(w, d),
    signature: { word: step.toUpperCase(), reach: `${reachOf(step, reach)} m` },
    rules: [...w.specialRules],
    conditional: (w.rules ?? []).map(ruleSentence),
    modules: moduleLines(w.recipe),
    area: areaFigure(w),
  };
}

/** A rack cell; `folds` ones give way on a narrow plate unless it is the armed one. */
interface RackCell extends PlateCell {
  folds?: boolean;
}

export interface RackPlateView {
  /** "RANGED", "MELEE · RELIC": the class word, then at most two of the weapon's tags. */
  kind: string;
  cells: RackCell[];
  /** The step's word and its arcs on the mark: silent none, deafening three. */
  signature: { word: string; arcs: number };
  /** Its modules, numbered and named ("① Silencer"); the armed plate lists them. */
  modules: string[];
  /** Its conditional rules as sentences; the armed plate lists them. */
  when: string[];
}

const ARCS: Record<Signature, number> = { silent: 0, muffled: 1, loud: 2, deafening: 3 };

/** The figures after damage and pen: reach and SB for a blade, range and rate of fire for a gun. */
const restOf = (s: Slot): RackCell[] =>
  s.weaponType === 'melee'
    ? [
        { label: 'REACH', value: `${s.reach || 1} m`, folds: true },
        { label: 'SB', value: `+${s.strengthBonus}`, folds: true },
      ]
    : [
        { label: 'RANGE', value: `${s.range} m`, folds: true },
        { label: 'ROF', value: rateOfFire(s.rateOfFire), folds: true },
      ];

/**
 * A weapon on the rack, in the data plate's grammar (plan armoury, part 7). At rest a forged weapon counts its
 * modules in a MODS cell (①②); `armed`, the plate names them instead. The rack's own facts - ammo, the parry and
 * Fury marks, the frame - stay the component's.
 */
export function rackPlateView(s: Slot, armed: boolean): RackPlateView {
  const step = signatureOf(s);
  const modules = moduleLines(s.recipe);
  const mods: RackCell[] =
    !armed && modules.length
      ? [{ label: 'MODS', value: modules.map((_, i) => numbered(i)).join(''), folds: true }]
      : [];
  return {
    kind: [
      s.weaponType === 'melee' ? 'MELEE' : 'RANGED',
      ...(s.tags ?? []).slice(0, 2).map((t) => t.toUpperCase()),
    ].join(' · '),
    cells: [
      { label: 'DAMAGE', value: `${s.damageExpression} ${s.damageType}`, lead: true },
      { label: 'PEN', value: String(s.penetration) },
      ...restOf(s),
      ...mods,
    ],
    signature: { word: step.toUpperCase(), arcs: ARCS[step] },
    modules,
    when: (s.rules ?? []).map(ruleSentence),
  };
}
